import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';

import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { Tour } from '../models/tour';
import { Message } from '../models/message';
import { TourStop } from '../models/tour-stop';
import { GeoLocation } from '../models/geo-location';
import { HomeDelivery } from '../models/home-delivery';
import { Transport } from '../models/transport';

import { Constants } from '../constants';

import { ApiService } from './api.service';
import { EventService } from './event.service';
import { LocationService } from './location.service';
import { LoggingService } from './logging.service';
import { MessageService } from './message.service';
import { ItemStatus } from '../models/item-status';
import { ModalService } from './modal.service';
import moment from 'moment';
import { ItemsByCategory } from '../models/items-by-category';
import { ScannerService } from "./scanner.service";
import { ContractStatus } from "../models/contract-status";

@Injectable({
  providedIn: 'root'
})
export class TourService {

  public shouldUseNotFoundTour = false;

  // the selected transport (Leitcode)
  public currentTransport: Transport;

  // indicates if the user needs to scan the transport start code (LC start code)
  public needsTransportStartCode = false;

  // eslint-disable-next-line no-underscore-dangle, id-blacklist, id-match
  private _tour: Tour; // internal tour, should only be accessed through the getter and setter functions

  private refreshErrorMessage: Message;

  private userSubscription: Subscription;
  private refreshTimerId: number;

  private showingTourRestartDialog = false;

  constructor(
    private api: ApiService,
    private events: EventService,
    private locationService: LocationService,
    private logger: LoggingService,
    private scanner: ScannerService,
    private messageService: MessageService,
    private translate: TranslateService,
    private auth: AngularFireAuth,
    private modal: ModalService,
  ) {
    this.restoreTour();
    this.userSubscription = this.auth.user.subscribe(u => {
      if (u) {
        // start polling for updates as soon as we're authenticated
        this.startPeriodicRefresh();
      } else {
        // stop polling for updates when we're no longer authenticated
        this.stopPeriodicRefresh();
      }
    });
    this.events.refreshButtonClicked.subscribe(() => {
      this.refresh(true);
    });
  }

  private startPeriodicRefresh(): void {
    if (this.refreshTimerId === undefined) {
      this.refreshTimerId = window.setInterval(() => this.refresh(), Constants.refreshTourIntervalInSeconds * 1000);
    }
  }

  private stopPeriodicRefresh(): void {
    if (this.refreshTimerId !== undefined) {
      clearInterval(this.refreshTimerId);
      this.refreshTimerId = undefined;
    }
  }

  // ==========================//
  // ===== Public getters =====//
  // ==========================//

  public get tourName(): string {
    return this.tour && this.tour.name;
  }

  public get tourStops(): TourStop[] {
    return this.tour && this.tour.tourStops || [];
  }

  public get tourItems(): ItemsByCategory[] {
    return this.tour && this.tour.items || [];
  }

  public get isStarted(): boolean {
    return this.tour !== undefined && this.tour !== null;
  }

  private get currentLocation(): GeoLocation {
    return this.locationService.currentLocation;
  }

  public get isComplete(): boolean {
    return this.tourStops.length === 0
      || this.tourStops.filter(tourStop => tourStop.status !== 'Error').length === 0; // TODO: create enum
  }

  // ========================== //
  // ===== Public helpers ===== //
  // ========================== //

  public isEndBarcode(barcode: string): boolean {
    return barcode && barcode === this.tour.endBarcode;
  }

  // ===========================//
  // ===== Tour operations =====//
  // ===========================//

  public restore(): Promise<void> {
    return this.restoreTour();
  }

  public refresh(userInitiated: boolean = false): Promise<boolean | void> {
    if (!this.isStarted || this.showingTourRestartDialog) {
      return Promise.resolve(false);
    }

    // DISPO-60: offer the user to restart the tour if it is out-of-date
    const startCode = this.getTourStartCode();
    const startDate = this.getTourStartDate();
    if (userInitiated && startCode && startDate) {
      // Adjust the daily time threshold to 3 o'clock in the morning due to night shifts on tours
      let timeThreshold = moment().startOf('day');
      if (moment().subtract(3, 'hours') < moment().startOf('day')) {
        timeThreshold = timeThreshold.subtract(1, 'day');
      }
      timeThreshold.add(3, 'hours');

      // if the started tour is older than the threshold, offer to restart it
      if (startDate.toDate() < timeThreshold.toDate() && userInitiated) {
        this.showingTourRestartDialog = true;
        return this.modal.showConfirmationModal(
          this.translate.instant('tour.reloadTourPromptTitle'),
          this.translate.instant('tour.reloadTourPromptText'),
          this.translate.instant('yes'),
          this.translate.instant('cancel'),
        )
          .then(result => {
            if (result) {
              this.endTour(this._tour.endBarcode)
                .then(() => this.startTour(startCode));
            }
          })
          .finally(() => {
            this.showingTourRestartDialog = false;
          });
      }
    }

    this.setLoading(true);
    return this.fetchUnreadMessages()
      .then(hasChanges => this.getTourStops()
        .then(() => hasChanges))
      .then(hasChanges => {
        this.setLoading(false);
        if (this.refreshErrorMessage) {
          this.messageService.remove(this.refreshErrorMessage);
          this.refreshErrorMessage = undefined;
        }

        if (!hasChanges && userInitiated) {
          const noChangesMessage = Message.success(this.translate.instant('tour.noTourChanges'));
          this.messageService.add(noChangesMessage, 2000);
        }

        return hasChanges;
      })
      .catch(someError => {
        this.setLoading(false);
        this.refreshErrorMessage = Message.warning(someError.toString());
        this.messageService.add(this.refreshErrorMessage);
      });
  }

  public fetchUnreadMessages(): Promise<boolean> {
    return this.messageService.getUnreadMessages();
  }

  public startTour(startBarcode: string): Promise<void> {
    this.setLoading(true);
    if (!startBarcode) {
      this.setLoading(false);
      return Promise.reject(new Error('Start barcode required to start tour!'));
    }

    return this.api.startTour(startBarcode, this.currentLocation)
      .then(tour => this.tour = tour)
      .then(() => this.recordTourStart(startBarcode, moment()))
      .then(() => this.scanner.currentTourName.next(this.tour.name))
      .then(() => this.saveTour())
      .then(() => this.fetchUnreadMessages())
      .then(() => this.getTourStops())
      .then(() => this.setLoading(false))
      .catch(error => {
        this.setLoading(false);
        return Promise.reject(error);
      });
  }

  public startTransport(startBarcode: string): Promise<Transport> {
    this.setLoading(true);
    if (!startBarcode) {
      this.setLoading(false);
      return Promise.reject(new Error('Start barcode required to start transport!'));
    }

    return this.api.startTransport(startBarcode, this.tour, this.currentLocation)
      .then(transport => this.api.getTransportStatus(transport, this.tour)
        .then(transportStatus => transport.setStatus(transportStatus)))
      .then(transport => {
        this.setLoading(false);
        return transport;
      })
      .catch(error => {
        this.setLoading(false);
        return Promise.reject(error);
      });
  }

  public getTransportStatus(transport: Transport): Promise<Transport> {
    this.setLoading(true);
    if (!transport) {
      this.setLoading(false);
      return Promise.reject(new Error('Transport is required to get the status of it!'));
    }

    return this.api.getTransportStatus(transport, this.tour)
      .then(transportStatus => transport.setStatus(transportStatus))
      .then(updatedTransport => {
        this.setLoading(false);
        return updatedTransport;
      })
      .catch(error => {
        this.setLoading(false);
        return Promise.reject(error);
      });
  }

  public scanTransportItem(barcode: string, transport: Transport): Promise<Transport> {
    this.setLoading(true);
    if (!barcode) {
      this.setLoading(false);
      return Promise.reject(new Error('Barcode is required to scan item!'));
    }

    if (!transport) {
      this.setLoading(false);
      return Promise.reject(new Error('Transport is required to scan item!'));
    }

    return this.api.scanTransportItem(barcode, transport, this.tour, this.currentLocation)
      .then(transportStatus => {
        this.setLoading(false);
        transport.setStatus(transportStatus);
        return transport;
      })
      .catch(error => {
        this.setLoading(false);
        return Promise.reject(error);
      });
  }

  public finishTransport(transport: Transport): Promise<void> {
    this.setLoading(true);
    if (!transport) {
      this.setLoading(false);
      return Promise.reject(new Error('Transport required to finish transport!'));
    }

    return this.api.finishTransport(transport, this.tour)
      .then(() => this.setLoading(false))
      .catch(error => {
        this.setLoading(false);
        return Promise.reject(error);
      });
  }

  public scanHomeDeliveryItem(barcode: string, homeDelivery: HomeDelivery): Promise<HomeDelivery> {
    this.setLoading(true);
    if (!barcode) {
      this.setLoading(false);
      return Promise.reject(new Error('Barcode is required to scan item!'));
    }

    if (!homeDelivery) {
      this.setLoading(false);
      return Promise.reject(new Error('Home delivery is required to scan item!'));
    }

    return this.api.scanHomeDeliveryItem(barcode, homeDelivery, this.tour, this.currentLocation)
      .then(contractStatus => {
        this.setLoading(false);
        homeDelivery.status = contractStatus.itemStatus;
        // TODO: add contract status to homedelivery
        return homeDelivery;
      })
      .catch(error => {
        this.setLoading(false);
        return Promise.reject(error);
      });
  }

  public getHomeDeliveryStatus(homeDelivery: HomeDelivery): Promise<HomeDelivery> {
    this.setLoading(true);
    if (!homeDelivery) {
      this.setLoading(false);
      return Promise.reject(new Error('Home delivery is required to get the status of it!'));
    }
    return this.api.getHomeDeliveryStatus(homeDelivery, this.tour)
      .then(itemStatus => homeDelivery.setItemStatus(itemStatus))
      .then(updatedHomeDelivery => {
        this.setLoading(false);
        return updatedHomeDelivery;
      })
      .catch(error => {
        this.setLoading(false);
        return Promise.reject(error);
      });
  }

  public finishHomeDelivery(homeDelivery: HomeDelivery): Promise<void> {
    this.setLoading(true);
    if (!homeDelivery) {
      this.setLoading(false);
      return Promise.reject(new Error('Home delivery is required to finish it!'));
    }

    return this.api.finishHomeDelivery(homeDelivery, this.tour)
      .finally(() => {
        this.setLoading(false);
      });
  }

  public increaseHomeDeliveryItemCount(homeDelivery: HomeDelivery, newItemCount): Promise<ItemStatus> {
    this.setLoading(true);
    if (!homeDelivery) {
      this.setLoading(false);
      return Promise.reject(new Error('Home delivery is required to increase item count!'));
    }

    const additionalItemCount = newItemCount - homeDelivery.status.total;
    if (additionalItemCount < 1) {
      this.setLoading(false);
      return Promise.reject(new Error('Can not decrease item count'));
    }
    return this.api.increaseHomeDeliveryItemCount(homeDelivery, this.tour, additionalItemCount)
      .then(itemStatus => {
        homeDelivery.status = itemStatus;
        return itemStatus;
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  public batchScanHomeDelivery(barcode: string, homeDelivery: HomeDelivery, actualNumberOfItems: number): Promise<ContractStatus> {
    this.setLoading(true);
    if (!homeDelivery) {
      this.setLoading(false);
      return Promise.reject(new Error('Home delivery is required for batch scan!'));
    }
    return this.api.batchScanHomeDelivery(barcode, homeDelivery, this.tour, this.currentLocation, actualNumberOfItems)
      .then(contractStatus => {
        homeDelivery.status = contractStatus.itemStatus;
        return contractStatus;
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  public endTour(barcode: string): Promise<void> {
    this.setLoading(true);
    if (!barcode) {
      this.setLoading(false);
      return Promise.reject(new Error('Barcode is required to end a tour'));
    }

    return this.api.finishTour(barcode, this.tour, this.currentLocation)
      .then(() => this.clearTour())
      .then(() => this.setLoading(false))
      .catch(error => {
        this.setLoading(false);
        return Promise.reject(error);
      });
  }

  public clearTour(): Promise<void> {
    return this.clearSavedTour()
      .then(() => {
        this._tour = undefined;
        this.scanner.currentTourName.next(null);
        this.messageService.removeMultiple(this.messageService.all);
        this.notify();
      });
  }

  private getTourStops(): Promise<void> {
    if (!this.tour) {
      return Promise.resolve();
    }

    return this.api.getTourStopsAndItems(this.tour)
      .then(tourStopsAndItems => this.setTourStopsAndItems(tourStopsAndItems[0], tourStopsAndItems[1]));
  }

  // ===== Tour helpers =====//

  private set tour(newTour: Tour) {
    this._tour = newTour;
    this.notify();
  }

  private get tour(): Tour {
    // if (Constants.isInDevelopment && this.shouldUseNotFoundTour) {
    //   this.logger.log('🔨 Returning custom tour!');
    //   return Tour.fromJSON({
    //     id: 'Route|12345678-FA28-40D6-95D3-8E18E771F36A|System.Guid',
    //     name: 'TOUR404',
    //     endBarcode: 'TOUR404',
    //   });
    // }
    return this._tour;
  }

  private setTourStopsAndItems(tourStops: TourStop[], items: ItemsByCategory[]): void {
    this._tour.tourStops = tourStops;
    this._tour.items = items;
    this.notify();
  }

  private getTourStartCode(): string {
    const tourStartCode = localStorage.getItem('tourStartCode');
    return tourStartCode === null ? undefined : tourStartCode;
  }

  private getTourStartDate(): moment.Moment {
    const tourStartDate = localStorage.getItem('tourStartDate');
    return tourStartDate === null ? undefined : moment(localStorage.getItem('tourStartDate'));
  }

  private recordTourStart(code: string, date: moment.Moment): void {
    localStorage.setItem('tourStartCode', code);
    localStorage.setItem('tourStartDate', date.toISOString());
  }

  private findTourStopForTransport(transport: Transport): TourStop | undefined {
    return this.tourStops.find(tourStop => tourStop.isTransport && tourStop.transport.matches(transport));
  }

  private notify(): void {
    this.events.tourChanged.next();
  }

  private setLoading(isLoading: boolean): void {
    this.events.isLoading.next(isLoading);
  }

  // ===== Tour persistence ===== //

  private saveTour(): Promise<void> {
    localStorage.setItem(Constants.LocalStorageKeys.startedTour, this.tour.json);
    return Promise.resolve();
  }

  private clearSavedTour(): Promise<void> {
    localStorage.removeItem(Constants.LocalStorageKeys.startedTour);
    return Promise.resolve();
  }

  private restoreTour(): Promise<void> {
    if (this.tour) {
      return Promise.resolve();
    }

    const stringJSON = localStorage.getItem(Constants.LocalStorageKeys.startedTour);
    if (!stringJSON) {
      this.logger.info(`No tour to restore from local storage`);
      return Promise.resolve();
    } else {
      try {
        const json = JSON.parse(stringJSON);
        this.tour = Tour.fromJSON(json);
        this.logger.info(`Restored tour from local storage`);
        this.scanner.currentTourName.next(this.tour.name);
        return Promise.resolve();
      } catch (error) {
        return Promise.resolve(); // if can't be restored, let's handle it as if it weren't there
      }
    }
  }
}
